import { AtividadesParams } from "@/repositorios/atividades/atividades.modelos"
import {
  CalendarioInterface,
  EnumeracaoDeNos,
  NoGenerico,
  NomeDosModulos,
} from "@/paginas/Atividades/CalendarioDeAtividades/CalendarioDeAtividades.modelos"
import { ArvoreDeSelecoes, ChaveValor, NoFilho } from "@/components/Gerais"
import { Ref, createApp, ref } from "vue"
import { usarFiltrosDoCalendarioStore } from "@/store/atividades/filtrosDoCalendario/filtrosDoCalendario.store"
import { usarProjetosStore } from "@/store/projetos/projetos.store"
import { storeToRefs, createPinia } from "pinia"
import { usarCalendarioStore } from "@/store/atividades/calendario/calendario.store"
import { usarDataHora } from "@/composable/UsarDataHora/usarDataHora"
import SessaoServico from "@/servicos/sessao.service"
import AppVue from "../../../App.vue"
import { usarLeitorDeDados } from "@/composable/UsarLeitorDeDados/usarLeitorDeDados"
import GruposDeProjetosRepositorio from "@/repositorios/gruposDeProjetos/gruposDeProjetos.repositorio"
import { ListarGruposDeProjetosSimples, ParametrosGruposDeProjetos } from "@/repositorios/gruposDeProjetos/gruposDeProjetos.modelo"
import { usarBuscarMaisDados } from "@/composable/UsarBuscarMaisDados/usarBuscarMaisDados"
import { EntidadeSimples } from "@/types/gerais"
import i18n from "@/i18n"

type FiltrosAgrupadosPorChave = {
  [key in NomeDosModulos]: ChaveValor<NomeDosModulos>[]
}

const { leitor, resolverLeitor } = usarLeitorDeDados()
const { buscarMaisDados, reinserirDadoPreSelecionadoNaLista } = usarBuscarMaisDados()

export const UTILIZAR_CACHE = true
export const NAO_UTILIZAR_CACHE = false

const leitorGruposDeProjetos = leitor<ListarGruposDeProjetosSimples,
  ParametrosGruposDeProjetos>(new GruposDeProjetosRepositorio().listarGruposDeProjetos, {
    ativo: "igual.true",
    "por-pagina": 30,
    "ordenar-por": "asc.nome",
  })


// Instanciar o pinia em testes para evitar erro de "getActivePinia was called with no active Pinia."
if (process.env.NODE_ENV === 'test') {
  const pinia = createPinia()
  const app = createApp(AppVue)
  app.use(pinia)
}

const { t } = i18n.global

const { filtrosProfissionais, filtrosProjetos, filtrosTiposDeAtividade } =
  storeToRefs(usarFiltrosDoCalendarioStore())

const { montarCalendarioDeAtividades, atualizarCalendarioLocalmente } =
  usarCalendarioStore()
const { buscarTodosProjetos } = usarProjetosStore()
const { projetosEntidadeGenerica } = storeToRefs(usarProjetosStore())

const { pegarSomenteData, pegarDataInicioEFimDaSemana, hoje, adicionarUmDia, subtrairUmDia } = usarDataHora()

const { inicioDaSemana, fimDaSemana } = pegarDataInicioEFimDaSemana(hoje)

const enumeracaoDeNos: EnumeracaoDeNos = {
  projeto: { key: "projeto",
    rotulo: t("projetos"),
    icone: "pi pi-map"
  },
  tiposDeAtividade: {
    key: "tiposDeAtividade",
    rotulo: t("tiposDeAtividade"),
    icone: "pi pi-calendar",
  },
  profissional: {
    key: "profissional",
    rotulo: t("profissionais"),
    icone: "pi pi-users",
  },
}

const opcaoDeTodosGrupos: EntidadeSimples = { nome: t("todosOsProjetos"), id: "todos-projetos" }

const noGenerico: NoGenerico = {
  children: [],
  $tamanho: function () {
    return this.children.length
  },
  $quantidadeFiltrada: function () {
    return this.children.filter((no) => no.contemNoFiltro).length
  },
}

// Funções
const agruparPorChave = (
  filtros: ChaveValor<NomeDosModulos>[]
  // eslint-disable-next-line @typescript-eslint/ban-types
): FiltrosAgrupadosPorChave | {} => {
  return filtros.reduce((grupo, filtro) => {
    const { chave } = filtro
    grupo[chave] = grupo[chave] ?? []
    grupo[chave].push(filtro)
    return grupo
  }, {})
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const listarIdsPorChave = (
  nomeDaChave: NomeDosModulos,
  // eslint-disable-next-line @typescript-eslint/ban-types
  filtrosAgrupadosPorChave: FiltrosAgrupadosPorChave | {}
) => {
  return filtrosAgrupadosPorChave[nomeDaChave].map((atual) => atual.valor)
}

const montarExpressaoEm = (ids: string[]): string | undefined =>
  ids.length ? `em.(${ids.join(",")})` : undefined

export const montarParametrosDeFiltros = (
  filtros: ChaveValor<NomeDosModulos>[]
): AtividadesParams => {
  const filtrosAgrupadosPorChave = agruparPorChave(filtros)

  const pegarListaDeIdsPorModulo = (modulo) => {
    if (filtrosAgrupadosPorChave[modulo]) {
      return listarIdsPorChave(modulo, filtrosAgrupadosPorChave)
    }
    return []
  }

  const idsProfissionais = pegarListaDeIdsPorModulo("profissional")
  const idsProjetos = pegarListaDeIdsPorModulo("projeto")
  const idsTiposDeAtividade = pegarListaDeIdsPorModulo("tiposDeAtividade")

  return {
    "por-pagina": "1000",
    id_profissional_que_realizou: montarExpressaoEm(idsProfissionais),
    id_projeto: montarExpressaoEm(idsProjetos),
    id_tipo_de_atividade: montarExpressaoEm(idsTiposDeAtividade),
  }
}

export const adicionarNaArvoreDeSelecoes = (
  arvoreDeFiltros: Ref<ArvoreDeSelecoes[]>,
  noFilho: NoFilho[],
  noPai: ArvoreDeSelecoes
): void => {
  const copiaArvoreDeFiltros = [...arvoreDeFiltros.value]
  const posicaoDoNoNaArvore = copiaArvoreDeFiltros.findIndex(
    (arvore) => arvore.key === noPai.key
  )
  const indiceNaoExiste = -1
  const noJaExisteNaArvore = posicaoDoNoNaArvore !== indiceNaoExiste

  if (noJaExisteNaArvore) {
    noPai.children = noFilho
    copiaArvoreDeFiltros.splice(posicaoDoNoNaArvore, 1, noPai)
    arvoreDeFiltros.value = copiaArvoreDeFiltros
  } else {
    noPai.children = noFilho
    arvoreDeFiltros.value.push(noPai)
  }
}

export const usuarioPossuiProfissionalAtivo = (): boolean =>
  SessaoServico.usuarioPossuiProfissionalAtivo

// Classes de perfil do calendário
abstract class Calendario implements CalendarioInterface {
  arvoreDeFiltros: Ref<ArvoreDeSelecoes[]>
  estaAtualizandoCalendario: Ref<boolean> = ref(false)

  constructor() {
    this.arvoreDeFiltros = ref([])
  }

  abstract inicializarCalendario(): Promise<void>

  abstract construirArvoreDeFiltros(): void

  async remontarCalendarioLocalmente(): Promise<void> {
    await atualizarCalendarioLocalmente()
  }

  async remontarCalendario(
    dataInicial: Date,
    dataFinal: Date,
    utilizaCache = true
  ): Promise<void> {
    await this.buscarAtividadesDoCalendario(
      dataInicial,
      dataFinal,
      utilizaCache
    )
  }

  buscarAtividadesDoCalendario = async (
    dataInicial: Date,
    dataFinal: Date,
    utilizaCache = true,
    // eslint-disable-next-line no-unused-vars
    filtros: ChaveValor<NomeDosModulos>[] = []
  ): Promise<void> => {
    const dataInicioFormatada = pegarSomenteData(dataInicial)
    const dataFimFormatada = pegarSomenteData(dataFinal)

    const parametros: AtividadesParams = {
      "por-pagina": "1000",
      pagina: "0",
      data_inicio: `entre.(${dataInicioFormatada},${dataFimFormatada})`,
    }

    try {
      this.estaAtualizandoCalendario.value = true
      await montarCalendarioDeAtividades(parametros, utilizaCache)
    } catch (erro) {
      throw new Error(t("ocorreuUmErroAoCarregarCalendario"))
    } finally {
      this.estaAtualizandoCalendario.value = false
    }
  }
}

export class CalendarioAdministrador
  extends Calendario
  implements CalendarioInterface {
  projetosSelecionados = ref<string[]>([])
  grupoDeProjetoSelecionado = ref<string>("")
  gruposDeProjetos = ref<EntidadeSimples[]>([])
  estaCarregandoGruposDeProjetos = ref<boolean>(false)


  constructor() {
    super()
  }

  inicializarCalendario = async (): Promise<void> => {
    this.estaAtualizandoCalendario.value = true
    await this.buscarGruposEProjetos()
    this.selecionarTodosOsProjetos()
    const filtrosDeProjetos = this.atribuirProjetosSelecionadosAosFiltros()
    await this.buscarAtividadesDoCalendario(
      inicioDaSemana,
      fimDaSemana,
      UTILIZAR_CACHE,
      filtrosDeProjetos
    )
    this.construirArvoreDeFiltros()
  }

  buscarGruposEProjetos = async (): Promise<void> => {
    this.estaCarregandoGruposDeProjetos.value = true
    await leitorGruposDeProjetos.executar()
    resolverLeitor(leitorGruposDeProjetos.resultado, {
      sucesso: (dados) => {
        const opcaoTodosGrupos = [opcaoDeTodosGrupos]
        const grupos = dados.map(grupo => { return { id: grupo.id, nome: grupo.nome } })
        this.gruposDeProjetos.value = [...opcaoTodosGrupos, ...grupos]
      },
      erro: () => {
        this.estaCarregandoGruposDeProjetos.value = false
        throw new Error(t("erroAoBuscarGruposDeProjetos"))
      },
    })
    this.estaCarregandoGruposDeProjetos.value = false

    this.grupoDeProjetoSelecionado.value = this.gruposDeProjetos.value[1]?.id

    await this.buscarProjetos(this.grupoDeProjetoSelecionado.value)
  }

  async remontarCalendario(
    dataInicial: Date,
    dataFinal: Date,
    utilizaCache = true
  ): Promise<void> {
    const filtrosDeProjetos = this.atribuirProjetosSelecionadosAosFiltros()
    await this.buscarAtividadesDoCalendario(
      dataInicial,
      dataFinal,
      utilizaCache,
      filtrosDeProjetos
    )
  }

  construirArvoreDeFiltros = (): void => {
    adicionarNaArvoreDeSelecoes(
      this.arvoreDeFiltros,
      filtrosTiposDeAtividade.value,
      {
        ...noGenerico,
        ...enumeracaoDeNos["tiposDeAtividade"],
      }
    )
    adicionarNaArvoreDeSelecoes(
      this.arvoreDeFiltros,
      filtrosProfissionais.value,
      {
        ...noGenerico,
        ...enumeracaoDeNos["profissional"],
      }
    )
  }

  selecionarTodosOsProjetos = (): void => {
    const projetos = projetosEntidadeGenerica.value

    this.projetosSelecionados.value = projetos.map((projeto) => {
      return projeto.id
    })
  }

  atribuirProjetosSelecionadosAosFiltros = (): ChaveValor<NomeDosModulos>[] => {
    return this.projetosSelecionados.value.map((idProjeto) => {
      return {
        valor: idProjeto,
        chave: "projeto",
      }
    })
  }

  buscarAtividadesDoCalendario = async (
    dataInicial: Date,
    dataFinal: Date,
    utilizaCache = true,
    filtros: ChaveValor<NomeDosModulos>[] = []
  ): Promise<void> => {
    try {
      this.estaAtualizandoCalendario.value = true
      const dataInicioFormatada = pegarSomenteData(subtrairUmDia(dataInicial))
      const dataFimFormatada = pegarSomenteData(adicionarUmDia(dataFinal))
      const parametros: AtividadesParams = {
        ...montarParametrosDeFiltros(filtros),
        pagina: "0",
        data_inicio: `entre.(${dataInicioFormatada},${dataFimFormatada})`,
      }

      await montarCalendarioDeAtividades(parametros, utilizaCache)
    } catch (erro) {
      throw new Error(t("ocorreuUmErroAoCarregarCalendario"))
    } finally {
      this.estaAtualizandoCalendario.value = false
    }
  }

  buscarMaisGruposDeProjetos = async (): Promise<void> => {
    await buscarMaisDados(leitorGruposDeProjetos, this.gruposDeProjetos)
    reinserirDadoPreSelecionadoNaLista(this.gruposDeProjetos, opcaoDeTodosGrupos)
  }

  buscarProjetos = async (idGrupoDeProjeto?: string): Promise<void> => {
    this.projetosSelecionados.value = []
    if (idGrupoDeProjeto) {
      await buscarTodosProjetos({
        grupo_de_projetos: `igual.${idGrupoDeProjeto}`
      }, ["ativo"])
    } else {
      await buscarTodosProjetos({}, ["ativo"])
    }
  }
}

export class CalendarioSupervisor
  extends Calendario
  implements CalendarioInterface {
  constructor() {
    super()
  }

  inicializarCalendario = async (): Promise<void> => {
    await this.buscarAtividadesDoCalendario(inicioDaSemana, fimDaSemana)
    this.construirArvoreDeFiltros()
    await buscarTodosProjetos()
  }

  construirArvoreDeFiltros = (): void => {
    adicionarNaArvoreDeSelecoes(this.arvoreDeFiltros, filtrosProjetos.value, {
      ...noGenerico,
      ...enumeracaoDeNos["projeto"],
    })
    adicionarNaArvoreDeSelecoes(
      this.arvoreDeFiltros,
      filtrosTiposDeAtividade.value,
      {
        ...noGenerico,
        ...enumeracaoDeNos["tiposDeAtividade"],
      }
    )
    adicionarNaArvoreDeSelecoes(
      this.arvoreDeFiltros,
      filtrosProfissionais.value,
      {
        ...noGenerico,
        ...enumeracaoDeNos["profissional"],
      }
    )
  }
}

export class CalendarioPessoal
  extends Calendario
  implements CalendarioInterface {
  constructor() {
    super()
  }

  inicializarCalendario = async (): Promise<void> => {
    await this.buscarAtividadesDoCalendario(inicioDaSemana, fimDaSemana)
    this.construirArvoreDeFiltros()
  }

  construirArvoreDeFiltros = (): void => {
    adicionarNaArvoreDeSelecoes(this.arvoreDeFiltros, filtrosProjetos.value, {
      ...noGenerico,
      ...enumeracaoDeNos["projeto"],
    })
    adicionarNaArvoreDeSelecoes(
      this.arvoreDeFiltros,
      filtrosTiposDeAtividade.value,
      {
        ...noGenerico,
        ...enumeracaoDeNos["tiposDeAtividade"],
      }
    )
  }
}

export class CalendarioVisualizador extends CalendarioAdministrador {
}
